/**
 * Token Manager
 * Handles storage, retrieval, and validation of authentication tokens
 * Now with encryption for enhanced security
 */

import { encryptTokens, decryptTokens, isEncryptionSupported } from './tokenEncryption';
import { logSecurityEvent, SecurityEventType } from './securityMonitor';

const TOKEN_STORAGE_KEY = 'auth_tokens';
const ENCRYPTED_TOKEN_STORAGE_KEY = 'auth_tokens_enc';
const USERNAME_STORAGE_KEY = 'auth_username';
const TENANT_ID_STORAGE_KEY = 'auth_tenant_id';

/**
 * Store tokens in localStorage (encrypted)
 * Also extracts and stores tenant ID separately for sync access
 * @param {Object} tokens - { accessToken, idToken, refreshToken, expiresIn }
 */
export const storeTokens = async (tokens) => {
  try {
    // Extract and store tenant ID separately (for synchronous access)
    if (tokens.idToken) {
      try {
        const payload = JSON.parse(atob(tokens.idToken.split('.')[1]));
        const tenantId = payload['custom:tenantId'];
        if (tenantId) {
          localStorage.setItem(TENANT_ID_STORAGE_KEY, tenantId);
        }
      } catch (error) {
        console.error('Failed to extract tenant ID from token:', error);
      }
    }

    // Store timestamp for token refresh tracking
    const tokensWithTimestamp = {
      ...tokens,
      storedAt: Date.now()
    };

    if (isEncryptionSupported()) {
      // Encrypt and store
      const encrypted = await encryptTokens(tokensWithTimestamp);
      localStorage.setItem(ENCRYPTED_TOKEN_STORAGE_KEY, encrypted);
      // Remove old unencrypted tokens if present
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    } else {
      // Fallback to unencrypted (should not happen in modern browsers)
      console.warn('Web Crypto API not available, storing tokens unencrypted');
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokensWithTimestamp));
    }
  } catch (error) {
    console.error('Failed to store tokens:', error);
    // Fallback to unencrypted storage
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  }
};

/**
 * Retrieve tokens from localStorage (decrypted)
 * @returns {Promise<Object|null>} Tokens or null if not found
 */
export const getStoredTokens = async () => {
  try {
    // Try encrypted tokens first
    const encryptedData = localStorage.getItem(ENCRYPTED_TOKEN_STORAGE_KEY);
    if (encryptedData && isEncryptionSupported()) {
      try {
        return await decryptTokens(encryptedData);
      } catch (decryptError) {
        console.error('Failed to decrypt tokens:', decryptError);

        // Log security event - possible tampering or device change
        logSecurityEvent(SecurityEventType.TOKEN_DECRYPTION_FAILURE, {
          error: decryptError.message
        });

        // Clear corrupted/tampered data
        localStorage.removeItem(ENCRYPTED_TOKEN_STORAGE_KEY);
        return null;
      }
    }

    // Fallback to unencrypted tokens
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (stored) {
      const tokens = JSON.parse(stored);
      // Migrate to encrypted storage
      if (isEncryptionSupported()) {
        await storeTokens(tokens);
      }
      return tokens;
    }

    // Fallback to old Amplify Cognito tokens (temporary - until Authenticator replaced)
    const clientId = import.meta.env.VITE_AWS_USER_POOLS_WEB_CLIENT_ID;
    const lastAuthUser = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`);
    if (lastAuthUser) {
      const idToken = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.idToken`);
      const accessToken = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.accessToken`);
      const refreshToken = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.refreshToken`);

      if (idToken && accessToken) {
        return {
          idToken,
          accessToken,
          refreshToken: refreshToken || null
        };
      }
    }
  } catch (error) {
    console.error('Failed to retrieve tokens:', error);
  }

  return null;
};

/**
 * Get tenant ID synchronously (for multi-tenant operations)
 * Reads from separately stored tenant ID
 * @returns {string|null} Tenant ID or null
 */
export const getTenantIdSync = () => {
  try {
    // Try custom auth tenant ID first
    const tenantId = localStorage.getItem(TENANT_ID_STORAGE_KEY);
    if (tenantId) return tenantId;

    // Fallback: try to decode from old Amplify Cognito tokens
    const clientId = import.meta.env.VITE_AWS_USER_POOLS_WEB_CLIENT_ID;
    const lastAuthUser = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`);
    if (lastAuthUser) {
      const idToken = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.idToken`);
      if (idToken) {
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        return payload['custom:tenantId'] || null;
      }
    }
  } catch (error) {
    console.error('[getTenantIdSync] Failed to get tenant ID:', error);
  }

  return null;
};

/**
 * Get ID token synchronously (for tenant ID extraction)
 * Only reads from unencrypted fallback or Amplify tokens
 * @returns {string|null} ID token or null
 * @deprecated Use getTenantIdSync() instead
 */
export const getIdTokenSync = () => {
  try {
    // Try unencrypted tokens first
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (stored) {
      const tokens = JSON.parse(stored);
      if (tokens.idToken) return tokens.idToken;
    }

    // Fallback to old Amplify Cognito tokens
    const clientId = import.meta.env.VITE_AWS_USER_POOLS_WEB_CLIENT_ID;
    const lastAuthUser = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`);
    if (lastAuthUser) {
      const idToken = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.idToken`);
      if (idToken) return idToken;
    }
  } catch (error) {
    console.error('[getIdTokenSync] Failed to get ID token:', error);
  }

  return null;
};

/**
 * Clear stored tokens
 */
export const clearTokens = () => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(ENCRYPTED_TOKEN_STORAGE_KEY);
  localStorage.removeItem(USERNAME_STORAGE_KEY);
  localStorage.removeItem(TENANT_ID_STORAGE_KEY);

  // Clean up old Amplify Cognito tokens
  const clientId = import.meta.env.VITE_AWS_USER_POOLS_WEB_CLIENT_ID;
  if (clientId) {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('CognitoIdentityServiceProvider')) {
        localStorage.removeItem(key);
      }
    });
  }
};

/**
 * Store username
 * @param {string} username
 */
export const storeUsername = (username) => {
  localStorage.setItem(USERNAME_STORAGE_KEY, username);
};

/**
 * Get stored username
 * @returns {string|null}
 */
export const getStoredUsername = () => {
  return localStorage.getItem(USERNAME_STORAGE_KEY);
};

/**
 * Decode JWT token payload
 * @param {string} token - JWT token
 * @returns {Object} Decoded payload
 */
export const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode token:', error);
    return {};
  }
};

/**
 * Check if token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if expired
 */
export const isTokenExpired = (token) => {
  const payload = decodeToken(token);
  if (!payload.exp) {
    return true;
  }

  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  return Date.now() >= expirationTime;
};

/**
 * Check if token is expiring soon (within 5 minutes)
 * @param {string} token - JWT token
 * @returns {boolean} True if expiring soon
 */
export const isTokenExpiringSoon = (token) => {
  const payload = decodeToken(token);
  if (!payload.exp) {
    return true;
  }

  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  const fiveMinutes = 5 * 60 * 1000;
  return Date.now() >= (expirationTime - fiveMinutes);
};

/**
 * Get time until token expiry (in milliseconds)
 * @param {string} token - JWT token
 * @returns {number} Milliseconds until expiry, or 0 if expired/invalid
 */
export const getTimeUntilExpiry = (token) => {
  const payload = decodeToken(token);
  if (!payload.exp) {
    return 0;
  }

  const expirationTime = payload.exp * 1000;
  const timeUntilExpiry = expirationTime - Date.now();
  return Math.max(0, timeUntilExpiry);
};

/**
 * Parse user attributes array to object
 * @param {Array} attributes - Array of {Name, Value} objects
 * @returns {Object} Attributes as key-value pairs
 */
export const parseAttributes = (attributes) => {
  return attributes.reduce((acc, attr) => {
    acc[attr.Name] = attr.Value;
    return acc;
  }, {});
};
